import React, { useState, useEffect } from 'react';
import { Card, Input, Select, Button, Space, Row, Col, Divider, Tag, DatePicker } from 'antd';
import { SearchOutlined, FilterOutlined, ClearOutlined, CalendarOutlined } from '@ant-design/icons';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ArticleTableFilter = ({ onApplyFilters, categories = [], subCategories = [] }) => {
  // Filter states
  const [titleSearch, setTitleSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [visibilityFilter, setVisibilityFilter] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // Update active filters count whenever any filter changes
  useEffect(() => {
    let count = 0;
    if (titleSearch) count++;
    if (categoryFilter.length > 0) count++;
    if (subCategoryFilter.length > 0) count++;
    if (statusFilter.length > 0) count++;
    if (visibilityFilter.length > 0) count++;
    if (dateRange) count++;
    setActiveFiltersCount(count);
  }, [titleSearch, categoryFilter, subCategoryFilter, statusFilter, visibilityFilter, dateRange]);

  // Apply filters
  const handleApplyFilters = () => {
    const filters = {
      titleSearch: titleSearch.trim(),
      categoryFilter,
      subCategoryFilter,
      statusFilter,
      visibilityFilter,
      dateRange
    };
    onApplyFilters(filters);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setTitleSearch('');
    setCategoryFilter([]);
    setSubCategoryFilter([]);
    setStatusFilter([]);
    setVisibilityFilter([]);
    setDateRange(null);
    onApplyFilters({
      titleSearch: '',
      categoryFilter: [],
      subCategoryFilter: [],
      statusFilter: [],
      visibilityFilter: [],
      dateRange: null
    });
  };

  // Status options
  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', color: '#faad14' },
    { value: 'APPROVED', label: 'Approved', color: '#52c41a' },
    { value: 'REJECTED', label: 'Rejected', color: '#f5222d' }
  ];

  // Visibility options
  const visibilityOptions = [
    { value: 'PUBLIC', label: 'PUBLIC', color: '#1890ff' },
    { value: 'PRIVATE', label: 'PRIVATE', color: '#722ed1' }
  ];
  return (
    <Card
      className="article-filter-card"
      title={
        <Space>
          <FilterOutlined />
          <span>Filter Articles</span>
          {activeFiltersCount > 0 && (
            <Tag color="blue">{activeFiltersCount} active</Tag>
          )}
        </Space>
      }
      extra={
        <Button
          icon={<ClearOutlined />}
          onClick={handleClearFilters}
          disabled={activeFiltersCount === 0}
        >
          Clear All
        </Button>
      }
      style={{ marginBottom: 16, borderRadius: 8 }}
    >
      <Row gutter={[16, 16]}>
        {/* Title Search */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Title</div>
            <Input
              placeholder="Search by title"
              prefix={<SearchOutlined />}
              value={titleSearch}
              onChange={(e) => setTitleSearch(e.target.value)}
              allowClear
            />
          </div>
        </Col>
  
        {/* Category Filter */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Category</div>
            <Select
              mode="multiple"
              placeholder="Filter by category"
              value={categoryFilter}
              onChange={setCategoryFilter}
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id} label={cat.name}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
  
        {/* Subcategory Filter */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Subcategory</div>
            <Select
              mode="multiple"
              placeholder="Filter by subcategory"
              value={subCategoryFilter}
              onChange={setSubCategoryFilter}
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {subCategories.map((subCat) => (
                <Option
                  key={subCat.id}
                  value={subCat.id}
                  label={subCat.name}
                >
                  {subCat.name}
                </Option>
              ))}
            </Select>
          </div>
        </Col>
  
        {/* Status Filter */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
            <Select
              mode="multiple"
              placeholder="Filter by status"
              value={statusFilter}
              onChange={setStatusFilter}
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {statusOptions.map((status) => (
                <Option
                  key={status.value}
                  value={status.value}
                  label={status.label}
                >
                  <Tag color={status.color} style={{ marginRight: 8 }}>
                    {status.label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </div>
        </Col>
  
        {/* Visibility Filter */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Visibility</div>
            <Select
              mode="multiple"
              placeholder="Filter by visibility"
              value={visibilityFilter}
              onChange={setVisibilityFilter}
              style={{ width: '100%' }}
              optionLabelProp="label"
            >
              {visibilityOptions.map((vis) => (
                <Option key={vis.value} value={vis.value} label={vis.label}>
                  <Tag color={vis.color} style={{ marginRight: 8 }}>
                    {vis.label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </div>
        </Col>
  
        {/* Date Range Picker */}
        <Col xs={24} md={8}>
          <div style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Published Date</div>
            <RangePicker
              style={{ width: '100%' }}
              suffixIcon={<CalendarOutlined />}
              value={dateRange}
              onChange={setDateRange}
              allowClear
            />
          </div>
        </Col>
      </Row>
  
      <Divider style={{ margin: '16px 0' }} />
  
      <Row justify="end">
        <Button
          type="primary"
          icon={<FilterOutlined />}
          onClick={handleApplyFilters}
        >
          Apply Filters
        </Button>
      </Row>
    </Card>
  );
  };
  
  export default ArticleTableFilter;